import React from 'react'
import { Zap } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import AIActionLogsViewer from '@/components/dashboard/AIActionLogsViewer'

interface AIUsageIndicatorProps {
  projectId?: string
  nodeId: string
  actionsTotal?: number
  className?: string
}

const AIUsageIndicator: React.FC<AIUsageIndicatorProps> = ({
  projectId,
  nodeId,
  actionsTotal = 0,
  className = ''
}) => {
  // Show indicator if there have been any AI actions on this node
  if (actionsTotal === 0) {
    return null
  }

  // Color coding based on usage volume
  const isHighUsage = actionsTotal >= 50
  const isMediumUsage = actionsTotal >= 20

  return (
    <Dialog>
      <DialogTrigger asChild>
        <div
          className={`
            flex items-center gap-1 
            px-2 py-1 
            rounded-xs 
            text-xs font-medium
            transition-all duration-200
            cursor-pointer hover:scale-105
            ${isHighUsage
              ? 'bg-red-100 text-red-700 border border-red-200 hover:bg-red-200'
              : isMediumUsage
                ? 'bg-yellow-100 text-yellow-700 border border-yellow-200 hover:bg-yellow-200'
                : 'bg-blue-100 text-blue-700 border border-blue-200 hover:bg-blue-200'
            }
            ${className}
          `}
          title={`AI Actions Used by this node: ${actionsTotal} total (Click to view detailed logs)`}
        >
          <Zap
            className={`
              h-3 w-3 
              ${isHighUsage
                ? 'text-red-500'
                : isMediumUsage
                  ? 'text-yellow-500'
                  : 'text-blue-500'
              }
            `}
          />
          <span>
            {actionsTotal}
          </span>
        </div>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            AI Action Logs - Node: {nodeId}
          </DialogTitle>
        </DialogHeader>
        <AIActionLogsViewer
          projectId={projectId}
          nodeId={nodeId}
          isEmbedded={true}
        />
      </DialogContent>
    </Dialog>
  )
}

export default AIUsageIndicator
